import moment from "moment-timezone";

import { isEmpty, startCase, toLower } from "lodash";
import { getFirstName, getLastName } from "./sessionStorage";

// isPickupPharmacyInfoBlank reports whether a local pickup pharmacy is missing
// any required field (name, address1, city, state, zip, phone, NCPDP ID). Any
// blank field means the order can fail downstream (e.g. a missing NCPDP ID), so
// the EMR flags the pharmacy card with a red border. Centralized here so every
// place that renders a pickup pharmacy card (the PA visit note and the standalone
// pharmacy panel) screens identically — keeping the check in one component but
// not the other is exactly how the border previously went missing.
export function isPickupPharmacyInfoBlank(pharmacyInfo) {
  const p = pharmacyInfo || {};
  return [
    p.pharmacyName,
    p.pharmacyAddress1,
    p.pharmacyCity,
    p.pharmacyState,
    p.pharmacyZip,
    p.pharmacyPhone,
    p.pharmacyNCPDPID,
  ].some((field) => isEmpty(field));
}

import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";

import {
  COLOR_KICK_BLUE,
  COLOR_KICK_LIGHT_BLUE,
  COLOR_KICK_LIGHT_PINK,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";

export function displayGender(value) {
  switch (value) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    case "X":
      return "Unspecified Gender";
    default:
      return "{UNKNOWN GENDER}";
  }
}

export function displayVisitType(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
      return "Performance Anxiety 🎤️";
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety - Follow Up 🎤️";
    case VISIT_TYPE_INSOMNIA:
      return "Insomnia 💤";
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return "Insomnia - Follow Up 💤";
    case VISIT_TYPE_GENERALIZED_ANXIETY:
      return "Generalized Anxiety 😨";
    case VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "Generalized Anxiety - Follow Up 😨";
  }
}

export function displayVisitTypeNoEmoji(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
      return "Performance Anxiety";
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety - Follow Up";
    case VISIT_TYPE_INSOMNIA:
      return "Insomnia";
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return "Insomnia - Follow Up";
  }
}

export function displayOrderTypeShort(visitOrderType) {
  switch (visitOrderType) {
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return "Pickup";
    case ORDER_TYPE_PROP_1_PACK:
      return "1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
      return "2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Pickup Follow";
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "1pk Follow";
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "2pk Follow";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return "1pk Flavored";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return "2pk Flavored";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return "1pk Flavored Follow";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return "2pk Flavored Follow";
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return "Sample Pack Flavored";
    default:
      return `{UNKNOWN VISIT ORDER TYPE: ${visitOrderType}}`;
  }
}

export function displayAge(birthday) {
  return moment.utc().diff(birthday, "years");
}
export function displayLongFormattedDateTime(date) {
  return moment.utc(date).format("llll");
}
export function displayShortFormattedDateTime(date) {
  return moment.utc(date).format("lll");
}
export function displayDateOnly(date) {
  return moment.utc(date).format("LL");
}
export function displayLongFormattedDateTimePacific(date) {
  return moment.tz(date, "America/Los_Angeles").format("llll z");
}
export function displayShortFormattedDateTimePacific(date) {
  return moment.tz(date, "America/Los_Angeles").format("lll z");
}
// displaySnoozeWakeLine renders when a snoozed visit comes back, for the line under the patient's
// name in the visits list: "Wakes today 7:00 PM", "Wakes tomorrow 9:00 AM", "Wakes Aug 13, 5:40 PM".
//
// The wake time used to live only in the snooze pill's tooltip — so the single fact you want about a
// sleeping visit ("when does this land back on me?") was the one fact the list wouldn't show without
// a hover. Today/tomorrow are named rather than dated because that is the distinction that changes
// what someone does: a visit waking tonight is this shift's problem.
//
// Everything is in Pacific, like every other timestamp in the EMR. Returns "" for a missing or Go
// zero-time value so callers can treat empty as "render nothing".
export function displaySnoozeWakeLine(snoozedUntil, now = undefined) {
  if (!snoozedUntil) {
    return "";
  }
  const wake = moment.tz(snoozedUntil, "America/Los_Angeles");
  if (!wake.isValid() || wake.year() < 1971) {
    return "";
  }
  const today = moment.tz(now, "America/Los_Angeles").startOf("day");
  const days = wake.clone().startOf("day").diff(today, "days");
  const time = wake.format("h:mm A");
  if (days === 0) {
    return `Wakes today ${time}`;
  }
  if (days === 1) {
    return `Wakes tomorrow ${time}`;
  }
  return `Wakes ${wake.format("MMM D")}, ${time}`;
}

export function displayCapitalize(str) {
  return startCase(toLower(str));
}
// A named month rather than "03/2026": the medications list is a column of
// dates read at a glance, and a numeric month there is one more thing to decode
// per row. The abbreviation is always three characters, so the column still
// lines up.
export function displayShortDate(date) {
  return moment.utc(date).format("MMM YYYY");
}
export function displayLongDate(date) {
  return moment.utc(date).format("MM/DD/YYYY");
}
// A named month with the day — "Feb 20, 2026". Same reasoning as
// displayShortDate: a column of dates is read at a glance, and a numeric month
// is one more thing to decode per row. Use where the day matters (subscription
// lifecycle dates); displayShortDate where it doesn't (medications).
export function displayMediumDate(date) {
  return moment.utc(date).format("MMM D, YYYY");
}

export function displayPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return match[1] + "-" + match[2] + "-" + match[3];
  }
  return phoneNumberString;
}

export function displayPhoneNumberParens(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return phoneNumberString;
}

export function displayMyFullNameFromSession() {
  return displayCapitalize(`${getFirstName()} ${getLastName()}`);
}

// export function displayImage(url) {
//   {
//     return axios
//       .get(url, {
//         responseType: "arraybuffer"
//       })
//       .then(response => new Buffer(response.data, "binary").toString("base64"));
//   }
// }

export function displayVisitStatus(status) {
  switch (status) {
    case "N":
      return "New";
    case "C":
      return "Patient Completed";
    case "R":
      return "Ready";
    case "P":
      return "Pending";
    case "PRAD":
      return "Pending Review Admin";
    case "PA":
      return "Missing Payment";
    case "PP":
      return "Missing Photos";
    case "PV":
      return "Pending Verification";
    case "PR":
      return "Awaiting Patient Reply";
    case "PT":
      return "Missing Photos - Retake";
    case "CC":
      return "Closed - Complete";
    case "CE":
      return "Closed - Error";
    case "CP":
      return "Closed - Referral PCP";
    case "CU":
      return "Closed - Referral Urgent";
    case "CPU":
      return "Closed - Payment Uncollectable";
    case "CR":
      return "Closed - Refunded";
    case "CL":
      return "Closed - Cancelled";
    case "CD":
      return "Closed - Duplicate";
    case "CS":
      return "Closed - Spam";
    case "CO":
      return "Closed - Other";
  }
  return "Status Unknown";
}

export function displayVisitNoteDisposition(disposition) {
  switch (disposition) {
    case "ROUTINE_VISIT":
      return "Routine Visit";
    case "REFERRAL_PRIMARY":
      return "Referral - PCP";
    case "REFERRAL_URGENT":
      return "Referral - Urgent";
    case "AWAITING_PATIENT_REPLY":
      return "Awaiting Reply";
    case "DUPLICATE_VISIT":
      return "Closed - Duplicate";
    case "REFUND_REQUESTED":
      return "Closed - Refund";
    case "PAYMENT_UNCOLLECTABLE":
      return "Closed - Payment Uncollectable";
    case "CANCELATION_REQUESTED":
      return "Closed - Cancelled";
    case "SPAM":
      return "Closed - Spam";
    case "OTHER":
      return "Other";
    case "ERROR":
      return "Error";
  }
}

export function displayVisitNoteDispositionChip(disposition) {
  const label = displayVisitNoteDisposition(disposition);
  switch (disposition) {
    case "ROUTINE_VISIT":
      return generateChip(label, "#30544f", "white");
    case "REFERRAL_PRIMARY":
      return generateChip(label, "#ffdd00", COLOR_KICK_BLUE);
    case "REFERRAL_URGENT":
      return generateChip(label, "#ffdd00", "#f25930");
    case "AWAITING_PATIENT_REPLY":
      return generateChip(label, "#ffb7ad", "#1e2347");
    case "DUPLICATE_VISIT":
      return generateChip(label, "#dddddd", "#1e2347");
    case "REFUND_REQUESTED":
      return generateChip(label, "#f25930", "white");
    case "PAYMENT_UNCOLLECTABLE":
      return generateChip(label, "#f25930", "white");
    case "CANCELATION_REQUESTED":
      return generateChip(label, "#f25930", "white");
    case "SPAM":
      return generateChip(label, "#8096ab", COLOR_KICK_BLUE);
    case "OTHER":
      return generateChip(label, "#8096ab", COLOR_KICK_BLUE);
    case "ERROR":
      return generateChip(label, "#8096ab", COLOR_KICK_BLUE);
  }
}
// displayVisitStatusChip renders the visit's status and nothing else. Snooze is rendered separately
// by <SnoozeChip> next to this chip — this function used to swallow the status entirely when a
// visit was snoozed, which made a sleeping Ready visit indistinguishable from a sleeping Pending
// one at exactly the moment you most want to know the difference.
export function displayVisitStatusChip(visitStatus) {
  const label = displayVisitStatus(visitStatus);
  switch (visitStatus) {
    case "N":
      return generateChip(label, "#8096ab", "white");
    case "R":
      return generateChip(label, COLOR_KICK_BLUE, "white");
    case "P":
      return generateChip(label, "#ffb7ad", "white");
    case "PRAD":
      return generateChip(label, "#e0adff", "1e2347");
    case "PR":
      return generateChip(label, "#ffb7ad", "#1e2347");
    case "CC":
      return generateChip(label, "#30544f", "white");
    case "PA":
      return generateChip(label, "#ffb7ad", "#1e2347");
    case "PP":
      return generateChip(label, "#ffb7ad", "white");
    case "PV":
      return generateChip(label, "#ffb7ad", "white");
    case "CP":
      return generateChip(label, "#ffdd00", COLOR_KICK_BLUE);
    case "CU":
      return generateChip(label, "#ffdd00", "#f25930");
    case "CD":
      return generateChip(label, "#dddddd", "#1e2347");
    case "CS":
      return generateChip(label, "#8096ab", COLOR_KICK_BLUE);
    case "CPU":
      return generateChip(label, "#f25930", "white");
    case "CR":
      return generateChip(label, "#f25930", "white");
    case "CL":
      return generateChip(label, "#f25930", "white");

    default:
      return generateChip(label, "#dddddd", "#1e2347");
  }
}
export function displayOrderTypeChip(orderType) {
  const label = displayOrderTypeShort(orderType);
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return generateChip(label, "#f2f9f7", "#1e2347");
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return generateChip(label, "#fff7e5", "#1e2347");
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return generateChip(label, "#ffdd00", "#1e2347");
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return generateChip(label, "#f2f9f7", "#1e2347");
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return generateChip(label, "#f2f9f7", "#1e2347");
  }
}
function generateChip(label, backgroundColor, color) {
  return (
    <Chip
      style={{
        backgroundColor: backgroundColor,
        color: color,
        fontFamily: "BasisGrotesquePro-Regular",
        margin: "2px",
      }}
      label={label}
    />
  );
}

export function isPickupOrder(visitOrderType) {
  switch (visitOrderType) {
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return true;
    default:
      return false;
  }
}

export function visitSummaryBackgroundColor(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return COLOR_KICK_LIGHT_PINK;

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return COLOR_KICK_LIGHT_BLUE;

    case VISIT_TYPE_GENERALIZED_ANXIETY:
    case VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "#ffede9";
  }

  return COLOR_KICK_LIGHT_BLUE;
}

export function renderOpenPhoneDeepLink(
  phoneNumber,
  isAdminOrAssistant = false,
) {
  if (!phoneNumber) {
    return "";
  }

  // return `openphone://dial?number=${phoneNumber}&from=4157236001&action=call`;
  return (
    <Link href={`tel:${phoneNumber}`}>
      {displayPhoneNumberParens(phoneNumber)}
    </Link>
  );
}
